import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Spacer, StackWrap, Button } from '@telus-uds/components-web';
import { Bank, CreditCard } from '@telus-uds/palette-allium/build/web/icons';

import { linkFormContext } from '../../contexts/linkFormContext';
import { TokenizedCardForm } from '../tokenized-card-form/TokenizedCardForm';
import { BankAccountForm } from '../bank-account-form/BankAccountForm';
import { PaymentMethodType } from '../../domain/enums';

import './MethodTypeSelection.css';

export const MethodTypeSelection = () => {
  const { t } = useTranslation();
  const context = useContext(linkFormContext);
  const {
    allowedPaymentMethods,
    selectedMethod,
    setSelectedMethod,
    setCardToken,
    setPlaidPublicToken,
    disclaimers,
    setDisclaimerConsents,
    setSignature,
  } = context;
  const [isOnlyMethod, setIsOnlyMethod] = useState(false);
  const [applicableDisclaimers, setApplicableDisclaimers] = useState([]);

  useEffect(() => {
    if (allowedPaymentMethods.length === 1) setIsOnlyMethod(true);
    if (!selectedMethod) setSelectedMethod(allowedPaymentMethods[0]);
  }, [allowedPaymentMethods]);

  useEffect(() => {
    setCardToken(null);
    setPlaidPublicToken(null);
    setSignature(undefined);
  }, [selectedMethod, setCardToken, setPlaidPublicToken, setSignature]);

  useEffect(() => {
    const applicableDisclaimers = disclaimers.filter((d) => d.paymentMethods.includes(selectedMethod));
    const initialConsents = {};
    applicableDisclaimers.forEach((d) => {
      if (d.consentRequired) initialConsents[d.id] = false;
    });
    setApplicableDisclaimers(applicableDisclaimers);
    setDisclaimerConsents(initialConsents);
  }, [disclaimers, selectedMethod, setDisclaimerConsents]);

  const methodButtons = {
    [PaymentMethodType.TokenizedCard]: {
      icon: CreditCard,
      text: t('paymentMethodSelect.tokenizedCard'),
    },
    [PaymentMethodType.BankAccountDebit]: {
      icon: Bank,
      text: t('paymentMethodSelect.bankAccountDebit'),
    },
  };

  return (
    <Box>
      {!isOnlyMethod && (
        <>
          <Spacer space={4} />
          <Typography variant={{ size: 'h4' }} tag="h4">
            {t('paymentMethodSelect.title')}
          </Typography>
          <Spacer space={3} />
          <StackWrap direction={'row'} gap={3} space={3}>
            {allowedPaymentMethods.map((m, i) => (
              <Button
                key={i}
                icon={methodButtons[m].icon}
                onPress={() => setSelectedMethod(m)}
                variant={{ width: 'responsive', size: 'small', priority: m === selectedMethod ? 'high' : 'low' }}
              >
                {methodButtons[m].text}
              </Button>
            ))}
          </StackWrap>
        </>
      )}
      <Spacer space={4} />
      <linkFormContext.Provider value={{ ...context, applicableDisclaimers }}>
        {selectedMethod === PaymentMethodType.TokenizedCard && <TokenizedCardForm />}
        {selectedMethod === PaymentMethodType.BankAccountDebit && <BankAccountForm />}
      </linkFormContext.Provider>
    </Box>
  );
};
